import React, { useEffect, useState, useCallback } from 'react';
import './App.css';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CourseProvider } from './contexts/CourseContext';
import { LoadScript, Libraries } from '@react-google-maps/api';
import { CourseHoleService } from './services/CourseHoleService';
// Debug components removed for production
import { suppressResizeObserverErrors } from './utils/errorHandlers';
const Layout = React.lazy(() => import('./components/layout/Layout'));
const AppRouter = React.lazy(() => import('./AppRouter'));

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
// Define libraries as a static constant to avoid unnecessary reloads
// Using proper typing for Google Maps libraries
const googleMapsLibraries: Libraries = ['marker'];

/**
 * Main App component
 * Uses enhanced architecture with CourseContext for state management
 */
const App: React.FC = () => {
  // State to track Google Maps loading status and retries
  const [mapsLoadError, setMapsLoadError] = useState<boolean>(false);
  const [mapsRetryCount, setMapsRetryCount] = useState<number>(0);
  // isLoadingMaps is used to conditionally display loading indicators
  const [, setIsLoadingMaps] = useState<boolean>(true); // We only need the setter function

  // Handle Maps load success
  const handleMapsLoaded = useCallback(() => {
    //console.log('Google Maps API loaded successfully');
    setIsLoadingMaps(false);
    setMapsLoadError(false);
  }, []);

  // Handle Maps load failure with retry mechanism
  const handleMapsError = useCallback(() => {
    //console.error(`Google Maps failed to load (attempt ${mapsRetryCount + 1})`);
    setMapsLoadError(true);
    setIsLoadingMaps(false);
    
    // Retry loading after a delay if we haven't exceeded max retries
    if (mapsRetryCount < 3) {
      const retryDelay = Math.pow(2, mapsRetryCount) * 1000; // Exponential backoff
      //console.log(`Retrying in ${retryDelay}ms...`);
      
      setTimeout(() => {
        setMapsRetryCount(prevCount => prevCount + 1);
        setIsLoadingMaps(true);
        setMapsLoadError(false);
      }, retryDelay);
    }
  }, [mapsRetryCount]);

  useEffect(() => {
    // Load cache on startup
    CourseHoleService.getInstance();
    
    // Save cache when app closes
    const handleBeforeUnload = () => {
      CourseHoleService.getInstance().saveCache();
    };
    
    // Suppress ResizeObserver errors
    suppressResizeObserverErrors();
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <CourseProvider>
          <LoadScript 
            googleMapsApiKey={googleMapsApiKey!} 
            libraries={googleMapsLibraries}
            onLoad={handleMapsLoaded}
            onError={handleMapsError}
            loadingElement={
              <div className="flex h-screen justify-center items-center">
                <div className="text-blue-600 text-lg">Ladataan karttaa...</div>
              </div>
            }
          >
            <React.Suspense fallback={<div className="flex h-screen justify-center items-center">
              <div className="text-blue-600 text-lg">Ladataan Ratahaukka-sovellusta...</div>
            </div>}>
              {mapsLoadError ? (
                <div className="p-6 bg-red-100 border border-red-400 text-red-700 rounded">
                  <h2 className="font-bold">Karttapalvelun lataus epäonnistui</h2>
                  <p className="mb-2">Google Maps API:n lataus epäonnistui. Kokeile päivittää sivu.</p>
                  <button 
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    onClick={() => window.location.reload()}
                  >
                    Päivitä sivu
                  </button>
                </div>
              ) : (
                <Layout>
                  <AppRouter />
                </Layout>
              )}
            </React.Suspense>
          </LoadScript>
        </CourseProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
